import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import supabaseService from '../services/supabaseService';
import { formatAmount, formatCents } from '../utils/money';
import '../assets/styles/voucher.css';

// "2029-08-18" → "August 2029". Split as text: a bare YYYY-MM-DD handed to
// new Date() is read as UTC midnight, which is the month before this one on the
// 1st of any month, from anywhere west of Greenwich.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const monthAndYear = (iso) => {
  const [y, m] = String(iso || '').slice(0, 10).split('-');
  const month = MONTHS[Number(m) - 1];
  return month && y ? `${month} ${y}` : '';
};

const fullDate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-');
  const month = MONTHS[Number(m) - 1];
  return month && y && d ? `${Number(d)} ${month} ${y}` : '';
};

// The botanical sprig from the approved design, drawn rather than placed as an
// image so it stays sharp coming off a printer at any size.
const Sprig = ({ className }) => (
  <svg className={className} viewBox="0 0 130 100" fill="none" aria-hidden="true">
    <path d="M2 4C22 20 44 40 62 58c14 14 26 28 34 40" stroke="#7FA98A" strokeWidth="1.1" strokeLinecap="round" />
    <path d="M22 20c-9-4-16-2-19 4 4 6 12 7 19-4Z" fill="#CFE0D2" />
    <path d="M22 20c1-9 6-14 13-14 2 7-3 13-13 14Z" fill="#BFD6C4" />
    <path d="M42 39c-10-4-17-1-20 5 5 6 13 6 20-5Z" fill="#CFE0D2" />
    <path d="M42 39c2-9 7-14 14-13 1 7-4 12-14 13Z" fill="#BFD6C4" />
    <path d="M62 58c-10-3-17 0-19 6 5 6 13 5 19-6Z" fill="#D7E4D9" />
    <path d="M62 58c3-9 8-13 15-12 1 7-5 12-15 12Z" fill="#C7DCCB" />
    <path d="M80 78c-9-3-16 1-18 7 6 5 13 4 18-7Z" fill="#DCE7DD" />
    <ellipse cx="12" cy="6" rx="6.5" ry="5" fill="#F0D3D3" />
    <ellipse cx="13" cy="6.5" rx="3" ry="2.3" fill="#E3B9BA" />
  </svg>
);

const VoucherPage = () => {
  const { code } = useParams();
  const [state, setState] = useState({ status: 'loading', voucher: null });

  // A body class rather than bare @media print rules: every stylesheet in a CRA
  // build lands in one file, so a rule that hides the site header when printing
  // would hide it when printing the price list too.
  useEffect(() => {
    document.body.classList.add('voucher-print-page');
    return () => document.body.classList.remove('voucher-print-page');
  }, []);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', voucher: null });

    supabaseService.getGiftVoucherByCode(code)
      .then(data => {
        if (!active) return;
        // Null is the ordinary answer for a typo or an old link, not a failure.
        setState(data
          ? { status: 'found', voucher: data }
          : { status: 'missing', voucher: null });
      })
      .catch(err => {
        console.error('Voucher lookup failed:', err);
        if (active) setState({ status: 'error', voucher: null });
      });

    return () => { active = false; };
  }, [code]);

  if (state.status === 'loading') {
    return (
      <div className="voucher-page">
        <p className="voucher-page-status">Finding that voucher…</p>
      </div>
    );
  }

  if (state.status !== 'found') {
    return (
      <div className="voucher-page">
        <div className="voucher-page-empty">
          {/* Two different things go wrong here and they need different words.
              A missing code is the visitor's problem to fix — check the
              characters. A failed lookup is mine, and telling them the code is
              wrong when my database is the thing that's down sends them hunting
              for a mistake they didn't make. */}
          <h1>
            {state.status === 'missing'
              ? "I can't find that voucher"
              : "I couldn't look that voucher up"}
          </h1>
          {state.status === 'missing' ? (
            <p>
              There's no voucher with the code <strong>{code}</strong>. It's usually a
              character that's been misread — the codes never use the letters O, I, L or U,
              so a nought is a zero-lookalike worth checking, and a 1 is often a 7.
            </p>
          ) : (
            <p>
              Something went wrong looking it up, rather than the code being wrong. Please
              try again in a moment.
            </p>
          )}
          <p>
            If it still won't come up, email me at{' '}
            <a href="mailto:viktoriashouseofbeauty@gmail.com">viktoriashouseofbeauty@gmail.com</a>{' '}
            with the code and I'll find it my end.
          </p>
        </div>
      </div>
    );
  }

  const v = state.voucher;

  // Spent or run out. This has to reach the card itself, not just the page:
  // the card is what gets printed, and a redeemed voucher printed clean is a
  // live-looking certificate for money that has already been spent.
  const voided = !!(v.redeemed || v.expired);

  // Part-spent is the opposite case: still live, still worth something, and
  // the number that matters is no longer the one it was bought for. Whatever
  // is left is what the card shows, with the original underneath it — a card
  // reading $200 when $115 is left is the one thing this must never do.
  const balance = formatCents(v.balance_cents);
  const partUsed = (!!v.part_used || v.status === 'part_used') && !v.redeemed && balance !== '';
  const heroAmount = partUsed ? balance : formatAmount(v.amount);

  return (
    <div className="voucher-page">
      <div className="voucher-page-head">
        <h1>Your gift voucher</h1>
        <p>Print it, or forward this page on — the code on it is what I need to see.</p>
        <button type="button" className="voucher-print-btn" onClick={() => window.print()}>
          <Printer size={17} aria-hidden="true" />
          Print this voucher
        </button>
      </div>

      {v.redeemed && (
        <p className="voucher-page-flag">
          This voucher has already been used{v.redeemed_at
            ? ` — ${new Date(v.redeemed_at).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'long', year: 'numeric' })}`
            : ''}.
        </p>
      )}

      {partUsed && !v.expired && (
        <p className="voucher-page-flag">
          Some of this voucher has been used already. There's ${balance} left on it,
          and the code stays the same, so keep this page or the printout for next time.
        </p>
      )}

      {!v.redeemed && v.expired && (
        <p className="voucher-page-flag">
          This voucher ran out on {fullDate(v.expires_at)}. Get in touch before you write it
          off — I'd rather talk about it than have it go to waste.
        </p>
      )}

      <div className="voucher-scroll">
        <div className={`voucher${voided ? ' voucher--void' : ''}`}>
          <Sprig className="sprig tl" />

          {/* aria-hidden: the same fact is already stated in plain words above
              the card, and a screen reader does not need it twice. */}
          {voided && (
            <span className="voucher-stamp" aria-hidden="true">
              {v.redeemed ? 'Already used' : 'Expired'}
            </span>
          )}

          <div className="voucher-inner">
            <img
              className="v-logo"
              src="/images/reducedPics/navbar_logo_transparent.webp"
              alt=""
              aria-hidden="true"
            />

            <h2 className="v-title">Gift Voucher</h2>

            <div className="rule">
              <i />
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 2c4 4 6 8 0 16-6-8-4-12 0-16Z" fill="#BFD6C4" />
                <path d="M10 4v13" stroke="#7FA98A" strokeWidth="0.8" strokeLinecap="round" />
              </svg>
              <i />
            </div>

            <div className="parties">
              {v.recipient_name && (
                <div className="party">
                  <span className="party-label">for</span>
                  <span className="party-value">{v.recipient_name}</span>
                </div>
              )}
              <div className="party">
                <span className="party-label">from</span>
                <span className="party-value">{v.purchaser_name}</span>
              </div>
            </div>

            <div className="amount">
              <span className="sym">$</span><span className="num">{heroAmount}</span>
            </div>

            {/* On the card, not just on the page above it: this is the copy
                that gets printed and handed over, and the big number on it has
                to be the money that is actually there. */}
            {partUsed && (
              <p className="amount-left">left of the original ${formatAmount(v.amount)}</p>
            )}

            <div className="v-foot">
              <div className="foot-col">
                <span className="foot-label">valid until</span>
                <span className="foot-value">{monthAndYear(v.expires_at)}</span>
              </div>
              <div className="foot-col">
                <span className="foot-label">voucher code</span>
                <span className="foot-value code">{v.code}</span>
              </div>
              {/* The QR is the point of the bottom-right corner, the way it is
                  on her printed vouchers — the recipient books from the card in
                  their hand without typing an address. SVG rather than the PNG
                  the email uses, because this one goes through a printer. */}
              <div className="foot-col foot-col--qr">
                <img
                  className="v-qr"
                  src="/images/booking-qr.svg"
                  alt="QR code that opens beautybyviktoria.com/book"
                />
                <span className="foot-label">Book via QR code</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="voucher-page-terms">
        <p>
          Redeemable against any treatment on my menu, in one visit or across several —
          I'll keep track of what's left. Valid until {fullDate(v.expires_at)}. Not
          exchangeable for cash.
        </p>
        <p>
          To use it, book as normal and mention the code when you arrive.
        </p>
      </div>
    </div>
  );
};

export default VoucherPage;
